import axios, { AxiosResponse } from 'axios';
import { noop } from 'lodash';
import {
  Log,
  UserProfile,
  User as BaseUser,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';

import * as apiSdk from '@gmm/sdk';

import { getConfig } from '../../environmentConfig';

export type ProfileStudentSharing = 'None' | 'District' | 'School';
export type Idps = 'local' | 'Clever' | 'Google' | 'classLink';
export type LowercaseIdps = Lowercase<Idps>;
export type AccountType = 'trial' | 'pilot' | 'paid';
export type SalesStageResponse = string | undefined;
export type RecognizedSalesStageResponseTypes =
  | 'No quote'
  | 'Invoiced'
  | 'Quoted';
export type ProviderTitle =
  | 'Local'
  | 'Google Classroom'
  | 'ClassLink'
  | 'Clever';
export type SalesStage = 'no_quote' | 'invoiced' | 'quoted';

export interface User extends BaseUser {
  profile: UserProfile & {
    acct_exp?: string;
    acct_type?: AccountType;
    idp?: Idps;
    permissions?: string;
    sales_stage?: SalesStageResponse;
    student_sharing?: ProfileStudentSharing;
    username?: string;
  };
}

let userManager: UserManager | null = null;
let userManagerPromise: Promise<UserManager> | null = null;

export const getUserManager = async (): Promise<UserManager> => {
  if (userManager) {
    return userManager;
  }

  if (userManagerPromise) {
    return userManagerPromise;
  }

  userManagerPromise = (async () => {
    const { origin } = window.location;
    const config = await getConfig();
    const userManagerSettings: UserManagerSettings = {
      authority: config.HALL_MONITOR_URL,
      client_id: 'teacherapp',
      clockSkewInSeconds: 15 * 60, // 15 minutes in seconds
      post_logout_redirect_uri: `${origin}/oidc/logout`,
      redirect_uri: `${origin}/oidc/login`,
      response_type: 'code',
      revokeTokensOnSignout: true,
      scope:
        'openid profile teacherapi email teacher:rw student:rw organization:ro',
      silent_redirect_uri: `${origin}/oidc/renew`,
      userStore: new WebStorageStateStore({
        store: window.sessionStorage,
      }),
    };

    userManager = new UserManager(userManagerSettings);

    return userManager;
  })();

  return userManagerPromise;
};

if (process.env.NODE_ENV === 'development') {
  Log.setLogger(console);
}

/**
 * Navigates to the root path ('/') by replacing the current browser history state,
 * then reloads the page. This effectively clears out the browser history and reloads
 * the application at the home page.
 */
const deletePresence = (): Promise<void | AxiosResponse<any>> => {
  const config = apiSdk.presence.destroy();

  return axios.request(config).catch(noop);
};

function goHome(): void {
  window.history.replaceState({}, '', '/');
  window.history.go();
}

export const signOut = async (): Promise<void> => {
  await deletePresence();
  const manager = await getUserManager();

  await manager.signoutRedirect();
  manager.clearStaleState();
};

export const signOutCallback = async (): Promise<void> => {
  try {
    const manager = await getUserManager();

    await manager.signoutRedirectCallback();
    manager.clearStaleState();
  } catch {
  } finally {
    goHome();
  }
};

export const silentRenewCallback = async (): Promise<void> => {
  const manager = await getUserManager();

  await manager.signinSilentCallback();
};

export const signInSilent = async (): Promise<void> => {
  const manager = await getUserManager();

  await manager.signinSilent();
};

export const idpToProviderTitleMap = (idp: LowercaseIdps): ProviderTitle => {
  switch (idp) {
    case 'clever':
      return 'Clever';
    case 'classlink':
      return 'ClassLink';
    case 'google':
      return 'Google Classroom';
    default:
      return 'Local';
  }
};
